import type { CreateResumeDto } from "@reactive-resume/dto";
import { CircleNotchIcon, FileTextIcon } from "@phosphor-icons/react";
import { cn, DEFAULT_TEMPLATE } from "@reactive-resume/utils";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useCreateResume } from "@/client/services/resume/create";

import { DashboardContent } from "../../components/layout/dashboard-content";
import { TemplateCard } from "../components/template-card";
import { TemplatesPageHeader } from "../components/templates-page-header";
import {
  filterTemplates,
  RESUME_TEMPLATE_CATEGORIES,
  RESUME_TEMPLATES,
  type DocumentTemplate,
  type TemplateCategory,
} from "../data/templates";

const getResumeTemplateName = (template: DocumentTemplate): CreateResumeDto["template"] => {
  const thumbnailFile = template.thumbnailUrl?.split("/").at(-1)?.replace(".webp", "");

  return (thumbnailFile ?? DEFAULT_TEMPLATE) as CreateResumeDto["template"];
};

const createResumeSlug = (template: DocumentTemplate) => {
  const normalizedName = template.name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  return `cv-${normalizedName}-${Date.now()}`;
};

export const ResumeTemplatesPage = () => {
  const navigate = useNavigate();

  const { createResume, loading } = useCreateResume();

  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory>("all");

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const [creationError, setCreationError] = useState<string | null>(null);

  const visibleTemplates = useMemo(
    () => filterTemplates(RESUME_TEMPLATES, selectedCategory),
    [selectedCategory],
  );

  const handleUseTemplate = async (template: DocumentTemplate) => {
    if (loading) {
      return;
    }

    setSelectedTemplateId(template.id);
    setCreationError(null);

    try {
      const resume = await createResume({
        title: `CV ${template.name}`,
        slug: createResumeSlug(template),
        visibility: "private",
        template: getResumeTemplateName(template),
      });

      await navigate(`/client/builder/${resume.id}`);
    } catch {
      setSelectedTemplateId(null);

      setCreationError(
        "La création du CV a échoué. Vérifiez que le serveur est lancé, puis réessayez.",
      );
    }
  };

  return (
    <DashboardContent>
      <section className="min-w-0 space-y-5">
        <TemplatesPageHeader
          title="Modèles de CV"
          description="Choisissez une mise en page professionnelle, puis personnalisez-la dans le créateur de CV."
          categories={RESUME_TEMPLATE_CATEGORIES}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />

        {creationError && (
          <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
            <p className="text-xs font-medium leading-5 text-rose-700">{creationError}</p>
          </div>
        )}

        {loading && selectedTemplateId && (
          <div
            role="status"
            className={cn(
              "flex items-center gap-2 rounded-xl",
              "border border-indigo-200 bg-indigo-50",
              "px-4 py-3 text-xs font-medium text-indigo-700",
            )}
          >
            <CircleNotchIcon aria-hidden size={17} className="animate-spin" />
            Création de votre CV…
          </div>
        )}

        {visibleTemplates.length > 0 ? (
          <div
            className={cn(
              /*
               * Mobile-first :
               * deux cartes compactes par ligne.
               */
              "grid grid-cols-2 gap-2.5",
              /*
               * Tablette et desktop.
               */
              "sm:gap-4",
              "md:grid-cols-3",
              "2xl:grid-cols-4",
            )}
          >
            {visibleTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                selected={selectedTemplateId === template.id}
                onSelect={(selectedTemplate) => {
                  void handleUseTemplate(selectedTemplate);
                }}
              />
            ))}
          </div>
        ) : (
          <div className="grid min-h-52 place-items-center rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center">
            <div>
              <span className="mx-auto grid size-11 place-items-center rounded-xl bg-indigo-50 text-indigo-600">
                <FileTextIcon aria-hidden size={23} weight="duotone" />
              </span>

              <p className="mt-3 text-[13px] font-semibold text-indigo-950">
                Aucun modèle disponible
              </p>

              <p className="mt-1 text-xs text-slate-500">Essayez une autre catégorie.</p>
            </div>
          </div>
        )}
      </section>
    </DashboardContent>
  );
};
